import { useQueries } from "@tanstack/react-query";
import { communityApi } from "@bw/shared/api";
import { useAuth } from "@bw/shared/hooks";
import type {
  CommunityChannelResponse,
  CommunityGameNightResponse,
  CommunityResponse,
} from "@bw/shared/types/generated";
import { queryKeys } from "../../../common/query/queryClient";

/** Joined communities whose channels and events the hub reads in parallel. */
export const HUB_COMMUNITY_LIMIT = 8;

export interface HubEvent {
  community: CommunityResponse;
  event: CommunityGameNightResponse;
}

export interface HubChannel {
  community: CommunityResponse;
  channel: CommunityChannelResponse;
}

/**
 * Community hub summary for the caller's joined communities: upcoming events
 * and unread channels. Reads run in parallel (never a waterfall), are bounded to
 * the first HUB_COMMUNITY_LIMIT joined communities, and share the exact cache
 * slots the community page uses, so opening a community is instant.
 */
export function useCommunityHubActivity(joined: readonly CommunityResponse[]) {
  const { user, loading } = useAuth();
  const enabled = Boolean(user?.uid) && !loading;
  const scoped = joined.slice(0, HUB_COMMUNITY_LIMIT);

  const channelQueries = useQueries({
    queries: scoped.map((community) => ({
      queryKey: [
        ...queryKeys.social.communityChannels(community.community_id),
        user?.uid ?? null,
      ],
      queryFn: () => communityApi.listChannels(community.community_id),
      enabled,
      retry: false,
    })),
  });
  const eventQueries = useQueries({
    queries: scoped.map((community) => ({
      queryKey: [
        ...queryKeys.social.communityGameNights(community.community_id, false),
        user?.uid ?? null,
      ],
      queryFn: () => communityApi.listGameNights(community.community_id, false),
      enabled,
      retry: false,
    })),
  });

  const unreadByCommunity = new Map<string, number>();
  const unreadChannels: HubChannel[] = [];
  scoped.forEach((community, index) => {
    const items = channelQueries[index]?.data?.items ?? [];
    let unread = 0;
    for (const channel of items) {
      if (channel.archived_at || channel.unread_count <= 0) continue;
      unread += channel.unread_count;
      unreadChannels.push({ community, channel });
    }
    unreadByCommunity.set(community.community_id, unread);
  });
  unreadChannels.sort((a, b) => b.channel.unread_count - a.channel.unread_count);

  const now = Date.now();
  const upcoming: HubEvent[] = scoped
    .flatMap((community, index) =>
      (eventQueries[index]?.data?.items ?? []).map((event) => ({ community, event })),
    )
    .filter(
      ({ event }) =>
        event.status === "published" && new Date(event.ends_at).getTime() > now,
    )
    .sort(
      (a, b) =>
        new Date(a.event.starts_at).getTime() - new Date(b.event.starts_at).getTime(),
    );

  return {
    unreadByCommunity,
    unreadChannels,
    upcoming,
    loading:
      channelQueries.some((query) => query.isLoading) ||
      eventQueries.some((query) => query.isLoading),
    failed:
      channelQueries.some((query) => query.isError) ||
      eventQueries.some((query) => query.isError),
  };
}
